import React, { useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../services/hrmsService";
import { FormFields, cleanValues } from "./hrForm";
import { EmptyState, LoadState, PrimaryButton, SecondaryButton } from "./hrComponents";
import { DetailGrid } from "./hrComponents";
import { cardStyle, formatDate, statusBadge, statusLabel, todayYmd, useLoad } from "./hrUi";

// EMP-08: leaving the company. A resignation goes to approval; HR can also record a termination or
// absconding, which take effect at once. The login is switched off the day after the last working day.
function ExitDrawer({ show, onHide, employeeId, isSelf, canManage, onDone }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    if (show) setValues({ separation_type: "RESIGNATION", request_date: todayYmd() });
  }, [show]);

  const types = [{ value: "RESIGNATION", label: "Resignation" }, ...(canManage ? [{ value: "TERMINATION", label: "Termination" }, { value: "ABSCONDING", label: "Absconding" }] : [])];
  const save = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.requestSeparation({ ...cleanValues(values), employee_id: employeeId });
      if (res?.success) {
        toast.success(res.message || "Recorded");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title={isSelf ? "Resign" : "Record an exit"}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving}>
            Submit
          </PrimaryButton>
        </>
      }
    >
      <FormFields
        values={values}
        onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
        fields={[
          { key: "separation_type", label: "Type", type: "select", required: true, options: types },
          { key: "request_date", label: "Date", type: "date" },
          { key: "last_working_day", label: "Last working day", type: "date", hint: "Leave blank to use the notice period." },
          { key: "reason", label: "Reason", type: "textarea", max: 1000 },
        ]}
      />
    </SlidePanel>
  );
}

export default function EmployeeExitTab({ employeeId, employee, isSelf, canManage, reload }) {
  const { data, loading, error, reload: reloadList } = useLoad(() => hrmsService.listSeparations(), [employeeId]);
  const [open, setOpen] = useState(false);
  const mine = (data || []).filter((s) => s.employee_id === employeeId);
  const active = mine.find((s) => ["PENDING_APPROVAL", "APPROVED"].includes(s.status));
  const canStart = (isSelf || canManage) && !active && !["SEPARATED", "ON_NOTICE"].includes(employee.status);

  const act = async (fn, okText) => {
    const res = await fn().catch((e) => ({ success: false, message: e.message }));
    if (res?.success) {
      toast.success(res.message || okText);
      reloadList();
      reload();
    } else toast.error(hrmsService.responseMessage(res));
  };

  return (
    <LoadState loading={loading} error={error} onRetry={reloadList}>
      {canStart ? (
        <div className="mb-3">
          <PrimaryButton onClick={() => setOpen(true)}>{isSelf ? "Resign" : "Record an exit"}</PrimaryButton>
        </div>
      ) : null}
      {mine.length === 0 ? (
        <EmptyState title="No exit on record" />
      ) : (
        mine.map((s) => (
          <div key={s.id} style={{ ...cardStyle, padding: 18, marginBottom: 12 }}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <strong>{statusLabel(s.separation_type)}</strong>
              <span style={statusBadge(s.status)}>{statusLabel(s.status)}</span>
            </div>
            <DetailGrid items={[["Date", formatDate(s.request_date)], ["Last working day", formatDate(s.last_working_day)], ["Notice period", s.notice_period_days ? `${s.notice_period_days} days` : null], ["Reason", s.reason]]} />
            <div className="mt-3 d-flex gap-2">
              {s.status === "PENDING_APPROVAL" ? (
                <SecondaryButton onClick={() => act(() => hrmsService.withdrawSeparation(s.id), "Withdrawn")}>Withdraw</SecondaryButton>
              ) : null}
              {s.status === "APPROVED" && canManage ? (
                <SecondaryButton onClick={() => act(() => hrmsService.cancelSeparation(s.id), "Cancelled")}>Cancel exit</SecondaryButton>
              ) : null}
            </div>
          </div>
        ))
      )}
      <ExitDrawer show={open} onHide={() => setOpen(false)} employeeId={employeeId} isSelf={isSelf} canManage={canManage} onDone={() => { reloadList(); reload(); }} />
    </LoadState>
  );
}
